import { defineStore } from 'pinia';
import {
  IPreorder,
  IStep1Data,
  MarketItemForClient,
  IStep2Data,
  IStep3Data,
  IStep4Data,
  IloginUserPreorderHistories,
  IToastItems,
  ICheckCertification,
  ICheckPreOrderRecord,
  ICreateCertification,
  IPreOrderRecord,
  IChangePhoneType,
  IChangeStore,
  IChangePhoneTypeResponse,
  IPreOrderRecordResponse,
  IError,
  IAgreements,
} from '@/components/modules/Interface/IPreorder';
import qs from 'qs';
import {
  validateForBirthDay,
  maskingUserInfo,
  formatCouponList,
  formattingInternationalNumber,
  isExistErrorCode,
  closeKeyboardScroll,
  INVALID_CODE_TARGET_CERTIFICATION,
  ALREADY_EXISTS_PHONENUMBER,
  ALREADY_EXISTS_EMAIL,
  DISABLE_CERTIFICATION_BY_EMAIL_LIMIT,
  DISABLE_CREATE_CERTIFICATION_BY_EMAIL_LIMIT,
  EXPIRED_COUNT_TARGET_CERTIFICATION,
  EXPIRED_COUNT_TARGET_CERTIFICATION_BY_EMAIL,
  DISABLE_CERTIFICATION_BY_PHONENUMBER_LIMIT,
  DISABLE_CERTIFICATION_BY_REMOTE_ADDR_LIMIT,
  EXISTS_BUT_DIFF_STORE,
  EMAIL_EXISTS_BUT_DIFF_STORE,
  BLOCKED_IP,
  NEED_CAPTCHA,
  CAPTCHA_FAILED,
  TOO_MANY_REQUEST,
  INVALID_DOMAIN,
} from '@/utils/preorderUtil';
import { i18n, tHtml } from '@/utils/i18n';
import { cloneDeep, assignIn, merge } from 'lodash';
import { getExtraData, getApiPathPrefix, convertBrTag } from '@/utils/utils';
import { usePreorderCommonStore as preorderCommonStore } from '@/store/preorderCommon';
import { axios } from '@/utils/axios';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { captchaInit } from '@/utils/captcha';
import { getSeviceEnv } from '@/utils/utils';
import { device } from '@/components/modules/common/module';
import {
  getGtmSuccessPreorder,
  pushGtmCustomEvent,
  pushMarkettingTracking,
} from '@/utils/gtmCustomEvent';

const { t } = i18n;

export const usePreorderStore = defineStore('preorder', {
  state: (): IPreorder => ({
    preorderStep: 1,
    birthDayInfo: { month: '', day: '', year: '' },
    keyboardInstance: closeKeyboardScroll(window.innerHeight),
    step1Data: {
      topMessage: '',
      agreements: [], // 약관 동의 목록
      ageLimit: 0, // 연령 제한
      useBirthdayInput: false, // 생년월일 입력 칸 사용 여부
    },
    step2Data: {
      topMessage: '',
      marketList: [], // 마켓 리스트
      useOptionalEmailField: false,
      selectedMarket: '', // 선택한 마켓  googleplay : 1, AppStore : 2, onestore : 4, galaxystore : 3, else: 1
      phoneNumber: '', // 전화번호 값
      emailStr: '', // 이메일 값
      optionalEmailStr: '', // (선택) 이메일 주소 값
      certificationCode: '', // 인증 코드 값
      selectedDrowpDown: '', // 드롭다운에서 선택한 국가코드 값
      couponKeyList: [], // 쿠폰 키 리스트
      clickSendCertificationCode: false, // 인증번호 발송 후 연속 클릭 방지
      isSendCertificationCode: false, // 인증번호 발송 여부
      isValidateCertificationCode: false, // 인증번호 발송 여부, 인증번호 인증 완료 여부
      isChangeMarket: false, // 마켓 변경 오류 유무
      phoneNumberFlag: true, // 휴대전화 유효성 검사 여부
    },
    step3Data: {
      topMessage: '',
      rewardList: [], // 보상 리스트
      selectedReward: [], // 선택한 보상
    },
    step4Data: {
      topMessage: '',
      finishMessage: '',
      completeButtonText: '홈으로 이동',
      extraButtonText: '추가 사전예약',
      goToHomeURL: '',
      couponList: [], // 사전예약한 쿠폰 리스트
      couponUserInfoList: [], // 전화번호, 이메일 선택 리스트
      couponRegisterDate: {
        couponRegisterDate: '',
        couponRegisterDateText: '', // 쿠폰 등록 기간 텍스트
        useStartTime: true,
        useEndTime: true,
        useDefaultText: true,
        utcString: '',
      },
      isAdditionalPreorder: false, // 추가 사전예약을 했는지 안했는지 여부
    },
    selected: [],
    selectAll: false,
    agreeList: [],
    marketList: [],
    toasts: [],
    toastTimer: null,
    agreeInput: null,
    monthInput: null,
    dayInput: null,
    yearInput: null,
    phoneOrEmailInput: null,
    certificationInput: null,
    optionsEmailInput: null,
    errorModal: null,
    preorderExtraData: '',
    captchaResult: '',
  }),
  getters: {
    getAddScrollEvent(state: IPreorder) {
      return device.value !== 'pc'
        ? () => state.keyboardInstance.addVisualViewportEvent(state.preorderStep)
        : () => {};
    },
    getTopMessage(state: IPreorder): number {
      return state.step1Data.ageLimit;
    },
    getAgeLimit(state: IPreorder): number {
      return state.step1Data.ageLimit;
    },
    getAgreeList(state: IPreorder): IAgreements[] {
      return state.agreeList;
    },
    getBirthDay(state: IPreorder) {
      return state.birthDayInfo;
    },
    // 쿠폰 타입 = IMMEDIATE,  useSelectReward = 보상 선택을 사용함, -> 쿠폰 선택 화면 노출
    getPreorderStep(): 3 | 2 {
      return preorderCommonStore().getCouponType === 'IMMEDIATE' &&
        preorderCommonStore().getUseSelectReward
        ? 3
        : 2;
    },
    // 필수인 약관
    getEssentialActiveAgreeList(state: IPreorder): IAgreements[] {
      return Object.values(state.agreeList).filter((agreeItem) => agreeItem.required === true);
    },
    // 필수인 약관 사항 중 선택된 약관
    getSelectedEssentialActiveAgreeList(state: IPreorder): IAgreements[] {
      return Object.values(state.agreeList).filter(
        (agreeItem) => agreeItem.required === true && agreeItem.checked === true
      );
    },
    // 선택 약관이 있는지 여부 있으면 true 없으면 false
    getHasSelectOptionalAgreeList(state: IPreorder): boolean {
      const optionalAgreeListLength = Object.values(state.agreeList).filter(
        (agreeItem) => agreeItem.required === false
      ).length;

      return optionalAgreeListLength !== 0;
    },
    // 선택 약관을 다 선택했는지 여부
    getSelectedOptionalAgreeList(state: IPreorder): boolean {
      const optionalAgreeListLength = Object.values(state.agreeList).filter(
        (agreeItem) => agreeItem.required === false
      ).length;
      const selectedOptionalAgreeListLength = Object.values(state.agreeList).filter(
        (agreeItem) => agreeItem.required === false && agreeItem.checked === true
      ).length;
      // 선택 약관이 있으면 모두 선택시 true
      return optionalAgreeListLength === selectedOptionalAgreeListLength;
    },

    // 전화번호, 이메일 입력칸 활성화 여부
    getStep2ButtonActive(state: IPreorder): boolean {
      // 마켓 사용시 선택된 마켓이 있는지
      // 미사용시 항상 true
      return preorderCommonStore().getUseSelectStore ? !state.step2Data.selectedMarket : false;
    },
    getStep2OptionsEmailInputActive(state: IPreorder): boolean {
      // 이메일 추가 입력란 (선택) 노출 조건
      // 전화번호 사전예약, 추가 이메일 입력창을 사용하고
      // 인증 사용하지 않거나, 마켓변경이 아닐 때 인증코드 입력이 끝나면
      return (
        preorderCommonStore().getUsePhone &&
        state.step2Data.useOptionalEmailField &&
        (!preorderCommonStore().getUseCertification ||
          (state.step2Data.isValidateCertificationCode && !state.step2Data.isChangeMarket))
      );
    },
    // 인증번호 전송 버튼 활성화 여부
    getStep2SendCerificateButtonActive(state: IPreorder): boolean {
      // 전송 안했으면 length나 마켓이 선택됨에따라
      if (preorderCommonStore().getUsePhone) {
        return (
          (preorderCommonStore().getUseSelectStore &&
            state.step2Data.selectedMarket.length === 0) ||
          state.step2Data.phoneNumber.length === 0
        );
      } else {
        return (
          (preorderCommonStore().getUseSelectStore &&
            state.step2Data.selectedMarket.length === 0) ||
          state.step2Data.emailStr.length === 0
        );
      }
    },
    getStep2CheckCertificateButtonActive(state: IPreorder): boolean {
      return state.step2Data.certificationCode.length === 0;
    },
    getPhoneType(state: IPreorder): string {
      return preorderCommonStore().getUseSelectStore ? state.step2Data.selectedMarket : '1';
    },
    getIsChangeMarket(state: IPreorder): boolean {
      return state.step2Data.isChangeMarket;
    },
    // 필수 약관 선택이 모두 선택되었는지 여부
    getSelectedEssentialAgreeList(): boolean {
      return (
        this.getSelectedEssentialActiveAgreeList.length === this.getEssentialActiveAgreeList.length
      );
    },

    // 최종 결정된 국가 번호 : 스테틱한 국가 번호 또는 드롭다운으로 선택한 국가 번호
    getInputedCountry(state: IPreorder) {
      // 지역코드를 사용하거나, 드랍다운을 사용하지 않을 경우 국가 코드는 운영툴에서 입력한 국가
      if (preorderCommonStore().getUsingAreaCode || !preorderCommonStore().getUsingDropDown)
        return preorderCommonStore().getSpecificCountryNumber;
      else {
        // 지역코드를 사용하지 않으면 드롭다운의 값이 곧 국가코드
        if (preorderCommonStore().getUsingDropDown) {
          return (
            state.step2Data.selectedDrowpDown || preorderCommonStore().getDropDownOptions[0].value
          );
        }
      }
    },

    // 최종 결정된 전화번호 : 지역번호 있는경우 지역번호 포함
    getInputedPhone(state: IPreorder) {
      if (preorderCommonStore().getUsingAreaCode)
        // 지역번호를 사용할때 드랍다운을 한번도 선택하지 않았다면 0번쨰 드랍다운의 값 + 입력한 전화번호값
        return (
          (state.step2Data.selectedDrowpDown || preorderCommonStore().getDropDownOptions[0].value) +
          state.step2Data.phoneNumber
        );
      else return state.step2Data.phoneNumber;
    },

    // 필수인 동의 사항을 모두 선택하고, 생년 월일도 모두 입력하면 버튼 활성화
    getStep1ButtonValidate(state: IPreorder): boolean {
      const agreeState =
        this.getSelectedEssentialActiveAgreeList.length === this.getEssentialActiveAgreeList.length;
      const birthState =
        Object.values(state.birthDayInfo).filter((birth) => birth.length === 0).length === 0;

      // 생년월일 사용 안할 경우 agreeState만 반환
      return this.step1Data.useBirthdayInput ? agreeState && birthState : agreeState;
    },
    // 스탭 2 에서 버튼 활성화 여부
    getStep2ButtonValidate(state: IPreorder): boolean {
      const noError =
        !preorderCommonStore().errorMessage.preorder.inputEmail &&
        !preorderCommonStore().errorMessage.preorder.inputPhone &&
        !preorderCommonStore().errorMessage.preorder.inputOptionsEmail &&
        !preorderCommonStore().errorMessage.preorder.inputCertify;
      // 인증 사용시
      // 인증번호가 전송되고 인증번호가 입력되면 활성화
      if (preorderCommonStore().getUseCertification) {
        return (
          state.step2Data.isValidateCertificationCode &&
          state.step2Data.certificationCode.length !== 0 &&
          noError
        );
      } else {
        // 인증 미사용시
        // 마켓 사용할 땐 마켓 선택하면 활성화
        // 마켓 미사용시 무조건 활성화
        return preorderCommonStore().getUseSelectStore
          ? state.step2Data.selectedMarket.length > 0 && noError
          : true;
      }
    },
    getStep3ButtonValidate(state: IPreorder): boolean {
      return (
        state.step3Data.rewardList.filter((rewardItem) => rewardItem.selected === true).length ===
        preorderCommonStore().getSelectableCouponCount
      );
    },
    // 인증번호 발송 API
    getApiUrlCreateCertification() {
      let result = '';
      if (preorderCommonStore().getUsePhone) {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/createPhoneNumberCertification`;
      } else {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/createEmailCertification`;
      }
      return result;
    },
    // 마켓 변경을 위한 인증번호 발송 API
    getApiUrlChangeStore() {
      let result = '';
      if (preorderCommonStore().getUsePhone) {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/createPhoneNumberCertificationForStore`;
      } else {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/createEmailCertificationForStore`;
      }
      return result;
    },
    // 인증번호 인증 API
    getApiUrlCheckCertification() {
      let result = '';
      if (preorderCommonStore().getUsePhone) {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/checkPhoneNumberCertification`;
      } else {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/checkEmailCertification`;
      }
      return result;
    },
  },
  actions: {
    setStep1Data(payload: IStep1Data): void {
      this.step1Data = assignIn({}, this.step1Data, payload);
    },
    setStep2Data(payload: IStep2Data): void {
      this.step2Data = assignIn({}, this.step2Data, payload);
      this.step2Data.couponKeyList = [];
    },
    setStep3Data(payload: IStep3Data): void {
      this.step3Data = assignIn({}, this.step3Data, payload);
    },
    setStep4Data(payload: IStep4Data): void {
      this.step4Data = assignIn({}, this.step4Data, payload);
    },
    setAgreeList(isReset?: boolean): void {
      this.agreeList = cloneDeep(this.step1Data.agreements);

      // 화면 사이즈 줄일 때 checked값이 없어짐
      this.agreeList.forEach((items, index) => {
        items.title = t(items.title, { age: this.step1Data.ageLimit });
        if (isReset || !items.checked) items.checked = false;
        if (Object.values(this.selected).findIndex((i) => i === index) !== -1) items.checked = true;
      });
    },
    setMarketList(isReset?: boolean) {
      this.marketList = assignIn([], this.marketList, cloneDeep(this.step2Data.marketList));
      this.marketList.forEach((marketItem: MarketItemForClient) => {
        if (isReset || !marketItem.selected) marketItem.selected = false;
      });
    },
    setBirthDayAndAgreeInputRef(payload: any): void {
      this.agreeInput = payload.agreeInput;
      this.monthInput = payload.monthInput;
      this.dayInput = payload.dayInput;
      this.yearInput = payload.yearInput;
    },
    setBirthDayInputData(id: 'year' | 'month' | 'day', text: string): void {
      this.birthDayInfo[id] = text;
    },
    setPhoneOrEmailInput(payload: HTMLInputElement | null): void {
      this.phoneOrEmailInput = payload;
    },
    setCertificationInput(payload: any): void {
      this.certificationInput = payload;
    },
    setOptionsEmail(payload: HTMLInputElement | null): void {
      this.optionsEmailInput = payload;
    },

    setErrorModal(payload: any): void {
      this.errorModal = payload.errorModal;
    },
    // 활성화된 동의 리스트와 선택한 리스트의 길이가 같으면 모두 선택된 것
    setSelectedAll() {
      this.selectAll = this.selected.length === this.step1Data.agreements.length;
      Object.values(this.agreeList).forEach((agreeItem) => {
        agreeItem.checked = this.selectAll;
      });
    },
    setLoginUserPhoneOrEmail(isReset?: boolean): void {
      // 로그인한 유저의 정보가 있을떄 삽입
      const loginUserInfo = preorderCommonStore().getLoginUserInfo;
      if (loginUserInfo) this._setLoginUserInfo(loginUserInfo);

      const loginUserPreorderHistories =
        preorderCommonStore().parentData.loginUserPreorderHistories;
      if (loginUserPreorderHistories && loginUserPreorderHistories.length !== 0) {
        this._checkAlreadyPreorder(loginUserPreorderHistories);
      }
      const { coupons } = preorderCommonStore().parentData.settings.coupon;

      // 선택한 보상이 유지되도록 설정
      this.step3Data.rewardList = coupons.map((coupon: any, index: number) => {
        const selected =
          isReset || !this.step3Data.rewardList[index]
            ? false
            : this.step3Data.rewardList[index].selected;

        return {
          rewardImage: coupon.pcImg ? coupon.pcImg : '',
          rewardImage_m: coupon.mobileImg ? coupon.mobileImg : '',
          rewardName: coupon.name,
          key: coupon.key,
          selected,
        };
      });
    },

    // 전화번호, 이메일을 input창에 미리 입력
    _setLoginUserInfo(loginUserInfo: { phoneNumber: string; email: string }) {
      const { phoneNumber = '', email = '' } = loginUserInfo;
      if (preorderCommonStore().getUseEmail && !this.step2Data.emailStr) {
        this.step2Data.emailStr = email;
      } else if (preorderCommonStore().getUsePhone && !this.step2Data.phoneNumber) {
        if (phoneNumber && isPossiblePhoneNumber(phoneNumber)) {
          const { nationalNumber, areaCode, countryCode, withOutAreaCodeNumber } =
            formattingInternationalNumber(phoneNumber);
          // 드롭다운을 지역코드로 사용할 때, 드롭다운을 국가코드로 사용할 때
          if (preorderCommonStore().getUsingAreaCode) {
            this.step2Data.selectedDrowpDown = areaCode;
            this.step2Data.phoneNumber = withOutAreaCodeNumber;
          } else {
            this.step2Data.selectedDrowpDown = countryCode;
            this.step2Data.phoneNumber = nationalNumber;
          }
        } else this.step2Data.phoneNumber = phoneNumber ? phoneNumber : '';
      }
    },

    // 이미 사전예약을 완료했는지 여부
    _checkAlreadyPreorder(
      loginUserPreorderHistories: Pick<
        IloginUserPreorderHistories,
        'email' | 'phoneNumber' | 'extraData'
      >[]
    ): void {
      if (this.step4Data.isAdditionalPreorder) return;
      this.preorderStep = 4;
      this.step4Data.couponList = [];
      Object.values(loginUserPreorderHistories).forEach((loginUserPreorderHistoryItem) => {
        const { extraData, phoneNumber, email } = loginUserPreorderHistoryItem;
        this.step4Data.couponList.push({
          originData: preorderCommonStore().getUsePhone ? phoneNumber : email,
          maskingInfo: maskingUserInfo(
            phoneNumber || '',
            email || '',
            preorderCommonStore().getLocale === 'zh_TW'
          ),
          couponList: formatCouponList(extraData),
        });
        this.step4Data.couponUserInfoList.push({
          value: (preorderCommonStore().getUsePhone ? phoneNumber : email) || '',
          maskingValue: maskingUserInfo(
            phoneNumber || '',
            email || '',
            preorderCommonStore().getLocale === 'zh_TW'
          ),
        });
      });
    },

    // 입력폼 1단계 유효성 검사
    validateInputStep1() {
      if (
        this.getSelectedEssentialActiveAgreeList.length !== this.getEssentialActiveAgreeList.length
      ) {
        preorderCommonStore().errorMessage.preorder.select_agree = t(
          'preorder.record.text.required'
        );
        this.agreeInput?.focus();
        return false;
      }
      // 생년월일 사용할 경우에만 체크
      if (!this.step1Data.useBirthdayInput) return true;

      preorderCommonStore().errorMessage.preorder.birth = validateForBirthDay(
        this.birthDayInfo.year,
        this.birthDayInfo.month,
        this.birthDayInfo.day,
        this.step1Data.ageLimit,
        this.yearInput,
        this.monthInput,
        this.dayInput,
        device.value === 'pc'
      );
      return preorderCommonStore().errorMessage.preorder.birth.length === 0;
    },

    // TOAST 메시지 트리거
    // 여러번 클릭시 메시지 유지하고 클릭 멈추면 토스트 메시지 사라짐
    triggerToast(payload: IToastItems): void {
      const setToastTimer = () => {
        this.toastTimer = setTimeout(() => {
          this.toasts.shift();
        }, 1000);
      };
      // 1개 이하이면 푸쉬
      if (this.toasts.length < 1) {
        this.toasts.push(payload);
        setToastTimer();
      } else {
        // 1개 이상이면 기존 타이머 지우고 없애고 다시 넣기
        this.toasts.shift();
        this.toasts.push(payload);
        if (this.toastTimer) {
          clearTimeout(this.toastTimer);
          this.toastTimer = null;
        }
        setToastTimer();
      }
    },

    // 인증번호 발송하기
    sendCertificationNumber(isAdmin: boolean = false) {
      if (isAdmin) {
        this.validateStep2ForAdmin();
      } else {
        this.sendCertification();
      }
    },

    // SMS 인증, 이메일 인증시 유효성 검사
    async sendCertification() {
      if (
        preorderCommonStore().getUsePhone &&
        !preorderCommonStore().validatePhoneNumber('preorder')
      )
        return;
      if (preorderCommonStore().getUseEmail && !preorderCommonStore().validateEmail('preorder'))
        return;
      this.step2Data.clickSendCertificationCode = true;

      let phoneParams: ICreateCertification = {
        gameName: preorderCommonStore().getGameName,
        phoneType: this.getPhoneType,
        phoneNumber: this.getInputedPhone,
        country: this.getInputedCountry,
      };
      let emailParams: ICreateCertification = {
        gameName: preorderCommonStore().getGameName,
        phoneType: this.getPhoneType,
        email: this.step2Data.emailStr,
      };

      // 캡차 데이터가 있으면 캡차 response를 보냄
      const hasCaptchData = typeof this.captchaResult === 'object';
      if (preorderCommonStore().getUsePhone) {
        if (this.captchaResult && hasCaptchData) {
          phoneParams = {
            ...phoneParams,
            clientKey: this.captchaResult.clientKey,
            response: this.captchaResult.response,
            type: this.captchaResult.type,
          };
        }
      } else {
        if (this.captchaResult && hasCaptchData) {
          emailParams = {
            ...emailParams,
            clientKey: this.captchaResult.clientKey,
            response: this.captchaResult.response,
            type: this.captchaResult.type,
          };
        }
      }
      const params = preorderCommonStore().getUsePhone ? phoneParams : emailParams;

      this.createCertification(params);
    },

    // 마켓 변경을 위한 인증번호 전송
    async changeStoreAndCreateCertification(payload: IChangeStore): Promise<void> {
      try {
        const response = await axios.post<string, boolean>(
          this.getApiUrlChangeStore,
          qs.stringify({ ...payload })
        );

        // 인증번호 전송후 타이머 시작
        preorderCommonStore().startTimer('preorder');
        this.step2Data.isValidateCertificationCode = true;
        this.step2Data.isSendCertificationCode = true;
        this.step2Data.certificationCode = '';
      } catch (error: any) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();
        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else if (isExistErrorCode(errorCode)) this.displayError(errorCode, errorMessage);
        else if (preorderCommonStore().getUsePhone) {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputPhone = errorMessage;
        } else if (preorderCommonStore().getUseEmail) {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputEmail = errorMessage;
        }
      }
      this.step2Data.clickSendCertificationCode = false;
    },

    // 인증 없을때 사전예약을 했는지 번호로 확인하는 api
    async checkPreOrderRecord(payload: ICheckPreOrderRecord): Promise<void> {
      try {
        const response = await axios.post<string, boolean>(
          `${getApiPathPrefix()}/preorder/preOrderRecord/checkPreOrderRecord`,
          qs.stringify({ ...payload })
        );
        // 인증 완료시
        // 보상 선택이 없으면 마지막 단계
        // 보상 선택이 있으면 다음 단계
        if (preorderCommonStore().getUseSelectReward) this.preorderStep++;
        else this.preorderStep += 2;
        preorderCommonStore().resetErrorMessage();
      } catch (error: any) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();
        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else if (isExistErrorCode(errorCode)) {
          this.displayError(errorCode, errorMessage);
        } else if (preorderCommonStore().getUseCertification) {
          this.certificationInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputCertify = errorMessage;
        } else if (preorderCommonStore().getUsePhone) {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputPhone = errorMessage;
        } else {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputEmail = errorMessage;
        }
      }
    },

    // SMS 인증 코드 발송 (사전 예약 시)
    // 이메일 인증 코드 발송 (사전 예약 시)
    async createCertification(payload: ICreateCertification): Promise<void> {
      try {
        const params = { ...payload };
        const response = await axios.post<string, boolean>(
          this.getApiUrlCreateCertification,
          qs.stringify(params)
        );
        // 인증번호 전송후 타이머 시작
        preorderCommonStore().resetErrorMessage();
        preorderCommonStore().startTimer('preorder');
        this.step2Data.isValidateCertificationCode = true;
        this.step2Data.isSendCertificationCode = true;
        this.step2Data.certificationCode = '';

        // 인증번호 입력창 focus
        setTimeout(() => {
          this.certificationInput?.click();
          this.certificationInput?.focus();
          this.certificationInput?.setSelectionRange(0, 0);
        }, 0);
      } catch (error) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();
        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else if (errorCode === NEED_CAPTCHA) {
          this.startCaptcha();
        } else if (isExistErrorCode(errorCode)) {
          this.displayError(errorCode, errorMessage);
        } else if (preorderCommonStore().getUsePhone) {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputPhone = errorMessage;
        } else if (preorderCommonStore().getUseEmail) {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputEmail = errorMessage;
        }
      }
      this.step2Data.clickSendCertificationCode = false;
    },

    // 인증번호 발송을 5초에 3번클릭(bot 으로 판단) (createEmailCertification)
    // 캡차 실행 > 캡차의 결과를 (createEmailCertification) body에 담아서 보냄
    async captchaTest() {
      const targetId: string = 'captchaTestWarp';
      // const env: 'rc-tw' | 'rc' | 'sb' | 'sandbox' | 'live' | 'tw' | 'op' | 'dev' = 'sb';
      const env = getSeviceEnv();

      if (!window.grecaptcha) {
        // wrapper 만들고
        const divEl = document.createElement('div');
        divEl.setAttribute('id', targetId);
        divEl.style.cssText = 'z-index: 99999; position: fixed; top: 20%;';
        document.body.prepend(divEl);
      }
      const option = { env, targetId };

      // 캡챠 관리 서버(captcha.plaync.com)에서 어떤 캡챠를 사용할건지 (google or wave) 받아오고
      // 캡챠를 사용할때 필요한 key를 받아오고,
      // 캡챠 사용 준비를 한다.
      const captchaObj = await captchaInit(option);

      return captchaObj;
    },

    async startCaptcha() {
      // 봇이라고 판단될 경우 캡차 실행
      const captchaObj = await this.captchaTest();
      if (!window.grecaptcha) await captchaObj!.showCaptcha();
      const captchaResult = await captchaObj!.execute();
      this.captchaResult = captchaResult;
      this.sendCertification();
      this.captchaResult = '';
      captchaObj?.refresh();
    },

    // SMS 인증코드 인증
    // 이메일 인증코드 인증
    async checkCertification(payload: ICheckCertification): Promise<void> {
      try {
        const params = { ...payload };
        const response = await axios.post<string, boolean>(
          this.getApiUrlCheckCertification,
          qs.stringify(params)
        );
        this.step2Data.isValidateCertificationCode = true;
        this.preorderStep++;
        preorderCommonStore().resetErrorMessage();
      } catch (error: any) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();
        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else if (isExistErrorCode(errorCode)) {
          this.displayError(errorCode, errorMessage);
        } else {
          this.certificationInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputCertify = errorMessage;
        }
      }
    },

    // 전화번호, 이메일 사전예약 저장
    async preOrderRecord(payload: IPreOrderRecord): Promise<void> {
      try {
        const params = { ...payload };

        const response = await axios.post<string, IPreOrderRecordResponse>(
          `${getApiPathPrefix()}/preorder/preOrderRecord`,
          qs.stringify(params)
        );
        // 인증 완료시
        // 보상 선택이 없으면 마지막 단계
        // 보상 선택이 있으면 다음 단계
        if (preorderCommonStore().getUseSelectReward) this.preorderStep++;
        else this.preorderStep += 2;

        const { extraData, phoneNumber, email = '' } = response.data;
        this.step4Data.couponList = [
          {
            originData: preorderCommonStore().getUsePhone ? phoneNumber : email,
            maskingInfo: maskingUserInfo(
              phoneNumber,
              email,
              preorderCommonStore().getLocale === 'zh_TW'
            ),
            couponList: formatCouponList(extraData),
          },
          ...this.step4Data.couponList,
        ];

        this.step4Data.couponUserInfoList = [
          {
            value: preorderCommonStore().getUsePhone ? phoneNumber : email,
            maskingValue: maskingUserInfo(
              phoneNumber,
              email,
              preorderCommonStore().getLocale === 'zh_TW'
            ),
          },
          ...this.step4Data.couponUserInfoList,
        ];

        // 퍼포먼스 관련 트래킹 코드 실행
        pushGtmCustomEvent(
          getGtmSuccessPreorder({
            appstore: this.getPhoneType,
            lang: document.documentElement.getAttribute('lang') ?? '',
            service: window._service,
            alias: window._alias,
          })
        );

        // 광고 관련 트래킹 코드 실행
        pushMarkettingTracking(response.data.idx);
      } catch (error: any) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();
        // 보상 선택화면(3단계)에서 오류 날 경우 2스탭으로 이동
        if (this.preorderStep === 3) this.preorderStep = 2;
        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else if (!preorderCommonStore().getUseCertification && errorCode === NEED_CAPTCHA) {
          // 인증이 없는 사전예약에서 캡차 실행
          this.startCaptcha();
        } else if (isExistErrorCode(errorCode)) {
          this.displayError(errorCode, errorMessage);
        } else if (preorderCommonStore().getUseCertification) {
          this.certificationInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputCertify = errorMessage;
        } else if (preorderCommonStore().getUsePhone) {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputPhone = errorMessage;
        } else if (preorderCommonStore().getUseEmail) {
          this.phoneOrEmailInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputEmail = errorMessage;
        }
      }
    },

    // 마켓 변경
    async changePhoneType(payload: IChangePhoneType): Promise<void> {
      try {
        const response = await axios.post<string, IChangePhoneTypeResponse>(
          `${getApiPathPrefix()}/preorder/preOrderRecord/changePhoneType`,
          qs.stringify({ ...payload })
        );
        // 마켓 변경시 바로 마지막단계로
        this.preorderStep += 2;

        // response 로 오는 extradata의 쿠폰을 this.step4Data.couponList 에서 이미 있으면 교체,
        // 없으면 새로 삽입
        const { extraData, phoneNumber, email = '' } = response.data;

        const compareData = preorderCommonStore().getUsePhone ? phoneNumber : email;
        const findExistIndex = this.step4Data.couponList.findIndex(
          (couponItem) => couponItem.originData === compareData
        );
        if (findExistIndex !== -1) {
          this.step4Data.couponList[findExistIndex] = {
            originData: preorderCommonStore().getUsePhone ? phoneNumber : email,
            maskingInfo: maskingUserInfo(
              phoneNumber,
              email,
              preorderCommonStore().getLocale === 'zh_TW'
            ),
            couponList: formatCouponList(extraData),
          };
        } else {
          this.step4Data.couponUserInfoList.push({
            value: preorderCommonStore().getUsePhone ? phoneNumber : email,
            maskingValue: maskingUserInfo(
              phoneNumber,
              email,
              preorderCommonStore().getLocale === 'zh_TW'
            ),
          });
          this.step4Data.couponList.push({
            originData: preorderCommonStore().getUsePhone ? phoneNumber : email,
            maskingInfo: maskingUserInfo(
              phoneNumber,
              email,
              preorderCommonStore().getLocale === 'zh_TW'
            ),
            couponList: formatCouponList(extraData),
          });
        }

        // 마켓 변경이 끝나면 상태 원복
        this.step2Data.isChangeMarket = false;
      } catch (error: any) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();
        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else if (isExistErrorCode(errorCode)) this.displayError(errorCode, errorMessage);
        else if (preorderCommonStore().getUseCertification) {
          this.certificationInput?.focus();
          preorderCommonStore().errorMessage.preorder.inputCertify = errorMessage;
        } else {
          if (preorderCommonStore().getUsePhone) {
            this.phoneOrEmailInput?.focus();
            preorderCommonStore().errorMessage.preorder.inputPhone = errorMessage;
          } else {
            this.phoneOrEmailInput?.focus();
            preorderCommonStore().errorMessage.preorder.inputEmail = errorMessage;
          }
        }
      }
    },

    // 프론트에서 관리하기 위한 에러 문구들
    displayError(errorCode: string | null | undefined, errorMessage: string = ''): void {
      this.phoneOrEmailInput?.blur();
      const _showErrorAndFocus = (isErrorModalMsg: boolean = false, useFocus: boolean = true) => {
        // 모달에서 나와야하는 메시지
        if (isErrorModalMsg) {
          preorderCommonStore().errorMessage.preorder.text = convertBrTag(errorMessage);
          return;
        }
        // 그외
        if (this.step2Data.isSendCertificationCode) {
          preorderCommonStore().errorMessage.preorder.inputCertify = convertBrTag(errorMessage);
          if (useFocus) this.certificationInput?.focus();
        } else {
          if (preorderCommonStore().getUsePhone) {
            preorderCommonStore().errorMessage.preorder.inputPhone = convertBrTag(errorMessage);
          } else {
            preorderCommonStore().errorMessage.preorder.inputEmail = convertBrTag(errorMessage);
          }
          if (useFocus) this.phoneOrEmailInput?.focus();
        }
      };

      const disableButton = () => {
        preorderCommonStore().errorMessage.preorder.disableCertificationButton = true;
        preorderCommonStore().stopTimer(preorderCommonStore().getTimer);
      };

      /*
      BLOCKED_IP 사전예약 신청이 제한된 IP입니다.

      EXPIRED_COUNT_TARGET_CERTIFICATION 동일 인증번호로 인증 실패가 계속되어 인증번호 발송이 차단되었습니다.
      EXPIRED_COUNT_TARGET_CERTIFICATION_BY_EMAIL 동일 인증번호로 인증 실패가 계속되어 인증번호 발송이 차단되었습니다.
      DISABLE_CERTIFICATION_BY_EMAIL_LIMIT 동일 메일로 인증 시도가 계속되어 인증번호 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요
      DISABLE_CERTIFICATION_BY_PHONENUMBER_LIMIT 동일 번호로 인증 시도가 계속되어 인증번호 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요.
      DISABLE_CERTIFICATION_BY_REMOTE_ADDR_LIMIT 동일 IP로 인증 시도가 계속되어 인증번호 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요.
      DISABLE_CREATE_CERTIFICATION_BY_EMAIL_LIMIT 동일 이메일 주소로 인증 시도가 계속되어 인증메일 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요.

      ALREADY_EXISTS_PHONENUMBER 사전예약 신청이 완료된 휴대전화입니다.
      ALREADY_EXISTS_EMAIL 사전예약 신청이 완료된 이메일 주소입니다.
      INVALID_CODE_TARGET_CERTIFICATION 인증번호가 올바르지 않습니다.
      INVALID_DOMAIN 사용할 수 없는 이메일 주소입니다.

      EMAIL_EXISTS_BUT_DIFF_STORE 다른 스토어로 사전예약 신청이 완료된 이메일 주소입니다. \n스토어를 변경하시겠습니까?
      EXISTS_BUT_DIFF_STORE 다른 스토어로 사전예약 신청이 완료된 휴대전화입니다. \n스토어를 변경하시겠습니까?

      CAPTCHA_FAILED 인증 실패가 계속되어 사전예약이 차단되었습니다.
      TOO_MANY_REQUEST 사전예약 신청 시도가 계속되어 사전예약이 차단되었습니다.
      */

      if (errorCode === BLOCKED_IP) {
        _showErrorAndFocus(true);
        disableButton();
        this.errorModal?.openModal();
      } else if (
        errorCode === EXPIRED_COUNT_TARGET_CERTIFICATION ||
        errorCode === EXPIRED_COUNT_TARGET_CERTIFICATION_BY_EMAIL ||
        errorCode === DISABLE_CERTIFICATION_BY_EMAIL_LIMIT ||
        errorCode === DISABLE_CERTIFICATION_BY_PHONENUMBER_LIMIT ||
        errorCode === DISABLE_CERTIFICATION_BY_REMOTE_ADDR_LIMIT ||
        errorCode === DISABLE_CREATE_CERTIFICATION_BY_EMAIL_LIMIT
      ) {
        _showErrorAndFocus(false, true);
        disableButton();
      } else if (
        errorCode === ALREADY_EXISTS_PHONENUMBER ||
        errorCode === ALREADY_EXISTS_EMAIL ||
        errorCode === INVALID_CODE_TARGET_CERTIFICATION ||
        errorCode === INVALID_DOMAIN
      ) {
        _showErrorAndFocus();
      } else if (errorCode === EMAIL_EXISTS_BUT_DIFF_STORE || errorCode === EXISTS_BUT_DIFF_STORE) {
        _showErrorAndFocus(true, false);
        preorderCommonStore().errorMessage.preorder.disableCertificationButton = false;
        this.step2Data.isChangeMarket = true;
        this.errorModal?.openModal();
      } else if (errorCode === CAPTCHA_FAILED || errorCode === TOO_MANY_REQUEST) {
        _showErrorAndFocus(true, false);
        disableButton();
        console.log('errorCode:', errorCode);
        console.log(preorderCommonStore().errorMessage.preorder.disableCertificationButton);
        this.errorModal?.openModal();
      }
    },

    setExtraData(): void {
      let options = {};
      // 전화번호 사전예약 이고 추가 이메일을 받을떄,
      if (preorderCommonStore().getUsePhone && this.step2Data.optionalEmailStr) {
        options = {
          optionsEmail: this.step2Data.optionalEmailStr,
          country: this.getInputedCountry,
        };
      } else {
        options = {
          country: this.getInputedCountry,
        };
      }

      let agreeConsignment;
      // 선택 약관이 있으면 선택했는지
      // 없으면 undefined
      if (this.getHasSelectOptionalAgreeList) {
        agreeConsignment = this.getSelectedOptionalAgreeList;
      } else agreeConsignment = undefined;

      this.preorderExtraData = getExtraData(options, agreeConsignment);
    },

    /*
    운영툴에서만 쓰는 함수들
    운영툴에서 확인을 위한 인증번호 함수
    */
    validateStep2ForAdmin(): void {
      if (
        preorderCommonStore().getUsePhone &&
        !preorderCommonStore().validatePhoneNumber('preorder')
      )
        return;
      if (preorderCommonStore().getUseEmail && !preorderCommonStore().validateEmail('preorder'))
        return;
      preorderCommonStore().startTimer('preorder');
      this.step2Data.isSendCertificationCode = true;
      this.step2Data.isValidateCertificationCode = true;
      this.step2Data.certificationCode = '';
    },

    setMockCouponData() {
      this.step4Data.couponUserInfoList = [
        {
          value: '01012345678',
          maskingValue: maskingUserInfo('01012345678', ''),
        },
        {
          value: '0935555888',
          maskingValue: maskingUserInfo('0935555888', '', true),
        },
        {
          value: 'test@ncsoft.com',
          maskingValue: maskingUserInfo('', 'test@ncsoft.com'),
        },
      ];
      this.step4Data.couponList = [
        {
          originData: '01012345678',
          maskingInfo: maskingUserInfo('01012345678', ''),
          couponList: formatCouponList(
            'couponNo=OOOOOOOOOOOOOO:혈석 2000개^OOOOOOOOOOOOOO:도화의 신석 패키지,'
          ),
        },
        {
          originData: '0935555888',
          maskingInfo: maskingUserInfo('0935555888', '', true),
          couponList: formatCouponList(
            'couponNo=OOOOOOOOOOOOOO:혈석 2000개^OOOOOOOOOOOOOO:도화의 신석 패키지,'
          ),
        },
        {
          originData: 'test@ncsoft.com',
          maskingInfo: maskingUserInfo('', 'test@ncsoft.com'),
          couponList: formatCouponList(
            'couponNo=XXXXXXXXXXXXXX:혈석 3000개^XXXXXXXXXXXXX:도화의 신석 패키지,'
          ),
        },
      ];
    },
  },
});
